// Geocoding utility using Nominatim (OpenStreetMap's free geocoding service)

export interface GeocodingResult {
  lat: number;
  lng: number;
  display_name: string;
  boundingbox?: [string, string, string, string]; // [south, north, west, east]
}

export interface GeocodingResponse {
  results: GeocodingResult[];
  query: string;
}

export interface ReverseGeocodingResult {
  display_name: string;
  address?: {
    house_number?: string;
    road?: string;
    neighbourhood?: string;
    suburb?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
  };
}

/**
 * Search for an address using Nominatim geocoding service
 */
export async function searchAddress(address: string): Promise<GeocodingResponse> {
  const query = `${address}, Frisco, Texas, USA`;
  const encodedQuery = encodeURIComponent(query);
  
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodedQuery}&limit=5&addressdetails=1&countrycodes=us`,
      {
        headers: {
          'User-Agent': 'Frisco-ISD-School-Zone-Map/1.0'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Geocoding service error: ${response.statusText}`);
    }

    const data = await response.json();
    
    const results: GeocodingResult[] = data
      .filter((item: any) => {
        // Filter to only include results in or near Frisco, Texas
        const displayName = item.display_name.toLowerCase();
        return displayName.includes('frisco') && displayName.includes('texas');
      })
      .map((item: any) => ({
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon),
        display_name: item.display_name,
        boundingbox: item.boundingbox
      }));

    return {
      results,
      query: address
    };
  } catch (error) {
    console.error('Geocoding error:', error);
    throw new Error('Unable to search for address. Please try again.');
  }
}

/**
 * Reverse geocode coordinates to get an address
 */
export async function reverseGeocode(lat: number, lng: number): Promise<ReverseGeocodingResult | null> {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&zoom=18`,
      {
        headers: {
          'User-Agent': 'Frisco-ISD-School-Zone-Map/1.0'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Reverse geocoding service error: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data || !data.display_name) {
      return null;
    }

    return {
      display_name: data.display_name,
      address: data.address
    };
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return null;
  }
}

/**
 * Format a reverse geocoding result into a concise address string
 */
export function formatAddress(result: ReverseGeocodingResult): string {
  const { address } = result;
  
  if (!address) {
    return result.display_name;
  }

  // Try to build a concise address
  const parts: string[] = [];
  
  if (address.house_number && address.road) {
    parts.push(`${address.house_number} ${address.road}`);
  } else if (address.road) {
    parts.push(address.road);
  }
  
  if (address.city || address.suburb) {
    parts.push(address.city || address.suburb || '');
  }
  
  if (address.state) {
    parts.push(address.state);
  }
  
  if (address.postcode) {
    parts.push(address.postcode);
  }

  return parts.filter(part => part.length > 0).join(', ') || result.display_name;
}

/**
 * Check if coordinates are within Frisco ISD boundaries (approximate)
 */
export function isWithinFriscoISD(lat: number, lng: number): boolean {
  // Approximate Frisco ISD boundaries
  const bounds = {
    north: 33.3,
    south: 33.05,
    east: -96.7,
    west: -97.0
  };

  return lat >= bounds.south && 
         lat <= bounds.north && 
         lng >= bounds.west && 
         lng <= bounds.east;
}

/**
 * Calculate distance between two points in kilometers
 */
export function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  return distance;
} 